// src/pages/auth/RegisterPage.tsx
import React, { useState } from 'react';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Loader } from 'lucide-react';
import { TermsModal } from '../../components/legal/TermsModal';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  agreeToTerms?: string;
  general?: string;
}

interface RegisterPageProps {
  onRegister?: (data: RegisterFormData) => Promise<void>;
  onNavigateToLogin?: () => void;
  onRegisterSuccess?: () => void;
  embedded?: boolean;
}

export const RegisterPage: React.FC<RegisterPageProps> = ({
  onRegister,
  onNavigateToLogin,
  onRegisterSuccess,
  embedded = false
}) => {
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });
  
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsModalTab, setTermsModalTab] = useState<'terms' | 'privacy'>('terms');

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      if (onRegister) {
        await onRegister(formData);
        if (onRegisterSuccess) {
          onRegisterSuccess();
        }
      } else {
        await new Promise(resolve => setTimeout(resolve, 2000));
        // Registration successful (mock mode)
        if (onRegisterSuccess) {
          onRegisterSuccess();
        }
      }
    } catch (error: any) {
      console.error('Registration error caught:', error);
      
      const errorMessage = error?.message || String(error);
      const errorCode = error?.code || error?.error?.code || '';
      
      let displayError = 'Registration failed. Please try again.';
      
      if (errorCode === 'EMAIL_EXISTS' || errorCode === 'USER_EXISTS' ||
          errorMessage.toLowerCase().includes('email already exists') ||
          errorMessage.toLowerCase().includes('user already exists') ||
          errorMessage.toLowerCase().includes('already registered')) {
        displayError = 'This email is already registered. Please use a different email or try logging in.';
      } else if (errorCode === 'WEAK_PASSWORD' || 
                 errorMessage.toLowerCase().includes('password is too weak')) {
        displayError = 'Password is too weak. Please use a stronger password with uppercase, lowercase, and numbers.';
      } else if (errorCode === 'RATE_LIMITED' ||
                 errorMessage.toLowerCase().includes('too many')) {
        displayError = 'Too many registration attempts. Please wait a few minutes before trying again.';
      } else if (errorCode === 'NETWORK_ERROR' || errorMessage.toLowerCase().includes('network')) {
        displayError = 'Network error. Please check your internet connection and try again.';
      } else if (errorMessage) {
        displayError = errorMessage;
      }
      
      setErrors({ general: displayError });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: embedded ? 'auto' : '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: embedded ? 'transparent' : 'var(--color-background)',
      padding: embedded ? '0' : 'var(--space-6)',
      fontFamily: 'var(--font-primary)'
    }}>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Custom checkbox styling for better visibility in dark mode */
        input[type="checkbox"].custom-checkbox {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          width: 16px;
          height: 16px;
          border: 2px solid var(--color-border);
          border-radius: 3px;
          cursor: pointer;
          position: relative;
          background-color: var(--color-background);
          transition: all 0.15s ease-in-out;
        }

        input[type="checkbox"].custom-checkbox:checked {
          background-color: #3b82f6;
          border-color: #3b82f6;
        }

        input[type="checkbox"].custom-checkbox:checked::after {
          content: '';
          position: absolute;
          left: 4px;
          top: 1px;
          width: 4px;
          height: 8px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }

        input[type="checkbox"].custom-checkbox:hover:not(:disabled) {
          border-color: #3b82f6;
        }

        input[type="checkbox"].custom-checkbox:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>

      <div style={{ width: '100%', maxWidth: '450px' }}>
        {/* Logo - only show when not embedded */}
        {!embedded && (
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }}>
            <h1 style={{
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--color-primary)',
              margin: '0 0 var(--space-2) 0',
              letterSpacing: 'var(--tracking-tight)'
            }}>
              heal
            </h1>
            <p style={{
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-base)',
              color: 'var(--color-primary)',
              margin: 0
            }}>
              Create your health companion account
            </p>
          </div>
        )}

        {/* Registration Form */}
        <div style={{
          backgroundColor: 'var(--color-background)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          padding: 'var(--space-8)'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            {/* General Error Message */}
            {errors.general && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)'
              }} className="animate-shake">
                <AlertCircle size={20} color="var(--color-error)" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <p style={{
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: '#991b1b',
                    margin: 0
                  }}>
                    {errors.general}
                  </p>
                  {errors.general.includes('already registered') && (
                    <button
                      type="button"
                      onClick={onNavigateToLogin}
                      style={{
                        fontFamily: 'var(--font-primary)',
                        fontSize: 'var(--text-xs)',
                        color: '#2563eb',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        marginTop: 'var(--space-1)',
                        padding: 0
                      }}
                    >
                      Go to login page
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" style={{
                display: 'block',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-primary)',
                marginBottom: 'var(--space-2)'
              }}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={20} color="var(--color-muted)" style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john.doe@example.com"
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    paddingLeft: 'var(--space-10)',
                    paddingRight: 'var(--space-4)',
                    paddingTop: 'var(--space-3)',
                    paddingBottom: 'var(--space-3)',
                    border: `1px solid ${errors.email ? 'var(--color-error)' : 'var(--color-border)'}`,
                    borderRadius: 'var(--radius-lg)',
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-text)',
                    outline: 'none',
                    transition: 'var(--transition-fast)',
                    backgroundColor: isLoading ? 'var(--color-surface)' : 'var(--color-background)',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    if (!errors.email) {
                      e.currentTarget.style.borderColor = 'var(--color-primary)';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = errors.email ? 'var(--color-error)' : 'var(--color-border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
              {errors.email && (
                <p style={{
                  marginTop: 'var(--space-1)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-error)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)'
                }}>
                  <AlertCircle size={16} />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" style={{
                display: 'block',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-primary)',
                marginBottom: 'var(--space-2)'
              }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={20} color="var(--color-muted)" style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }} />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Create a strong password"
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    paddingLeft: 'var(--space-10)',
                    paddingRight: 'var(--space-12)',
                    paddingTop: 'var(--space-3)',
                    paddingBottom: 'var(--space-3)',
                    border: `1px solid ${errors.password ? 'var(--color-error)' : 'var(--color-border)'}`,
                    borderRadius: 'var(--radius-lg)',
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-text)',
                    outline: 'none',
                    transition: 'var(--transition-fast)',
                    backgroundColor: isLoading ? 'var(--color-surface)' : 'var(--color-background)',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    if (!errors.password) {
                      e.currentTarget.style.borderColor = 'var(--color-primary)';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = errors.password ? 'var(--color-error)' : 'var(--color-border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  style={{
                    position: 'absolute',
                    right: 'var(--space-3)',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 'var(--space-1)',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--color-muted)',
                    transition: 'var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-secondary)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-muted)'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p style={{
                  marginTop: 'var(--space-1)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-error)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)'
                }}>
                  <AlertCircle size={16} />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" style={{
                display: 'block',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-primary)',
                marginBottom: 'var(--space-2)'
              }}>
                Confirm Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={20} color="var(--color-muted)" style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }} />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    paddingLeft: 'var(--space-10)',
                    paddingRight: 'var(--space-12)',
                    paddingTop: 'var(--space-3)',
                    paddingBottom: 'var(--space-3)',
                    border: `1px solid ${errors.confirmPassword ? 'var(--color-error)' : 'var(--color-border)'}`,
                    borderRadius: 'var(--radius-lg)',
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-text)',
                    outline: 'none',
                    transition: 'var(--transition-fast)',
                    backgroundColor: isLoading ? 'var(--color-surface)' : 'var(--color-background)',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    if (!errors.confirmPassword) {
                      e.currentTarget.style.borderColor = 'var(--color-primary)';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = errors.confirmPassword ? 'var(--color-error)' : 'var(--color-border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                  style={{
                    position: 'absolute',
                    right: 'var(--space-3)',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 'var(--space-1)',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--color-muted)',
                    transition: 'var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-secondary)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-muted)'}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p style={{
                  marginTop: 'var(--space-1)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-error)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)'
                }}>
                  <AlertCircle size={16} />
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Terms & Conditions Agreement */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                cursor: 'pointer',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-lg)',
                backgroundColor: errors.agreeToTerms ? '#fef2f2' : 'transparent',
                border: `1px solid ${errors.agreeToTerms ? 'var(--color-error)' : 'transparent'}`
              }}>
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="custom-checkbox"
                  style={{
                    marginTop: '2px',
                    flexShrink: 0
                  }}
                />
                <span style={{
                  marginLeft: 'var(--space-2)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-secondary)',
                  lineHeight: '1.5'
                }}>
                  I agree to the{' '}
                  <a
                    href="#terms"
                    onClick={(e) => {
                      e.preventDefault();
                      setTermsModalTab('terms');
                      setShowTermsModal(true);
                    }}
                    style={{
                      color: '#667eea',
                      textDecoration: 'underline',
                      fontWeight: 'var(--font-medium)',
                      cursor: 'pointer'
                    }}
                  >
                    Terms & Conditions
                  </a>
                  {' '}and{' '}
                  <a
                    href="#privacy"
                    onClick={(e) => {
                      e.preventDefault();
                      setTermsModalTab('privacy');
                      setShowTermsModal(true);
                    }}
                    style={{
                      color: '#667eea',
                      textDecoration: 'underline',
                      fontWeight: 'var(--font-medium)',
                      cursor: 'pointer'
                    }}
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>
              {errors.agreeToTerms && (
                <p style={{
                  marginTop: 'var(--space-2)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-error)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  paddingLeft: 'var(--space-3)'
                }}>
                  <AlertCircle size={16} />
                  {errors.agreeToTerms}
                </p>
              )}
            </div>

            {/* Create Account Button */}
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-background)',
                backgroundColor: isLoading ? 'var(--color-muted)' : 'var(--color-primary)',
                border: 'none',
                borderRadius: 'var(--radius-lg)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-fast)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = '#1f2937';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = 'var(--color-primary)';
                }
              }}
            >
              {isLoading ? (
                <>
                  <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </button>

            {/* Login Link */}
            <div style={{
              textAlign: 'center',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid var(--color-border)'
            }}>
              <span style={{
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-secondary)'
              }}>
                Already have an account?{' '}
              </span>
              <button
                type="button"
                onClick={onNavigateToLogin}
                disabled={isLoading}
                style={{
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: '#667eea',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'var(--font-medium)',
                  textDecoration: 'none',
                  transition: 'var(--transition-fast)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                Sign In
              </button>
            </div>
          </div>
        </div>

        {/* Terms Modal */}
        <TermsModal
          isOpen={showTermsModal}
          onClose={() => setShowTermsModal(false)}
          initialTab={termsModalTab}
        />
      </div>
    </div>
  );
};